// @flow @jsx h

import { Component, h } from 'preact'
import { ViewportIntersections } from '@nrk/dh-feature-components'
import { getScrollTop, bem } from '@nrk/dh-utils'
import { Map } from '../../shared/Map/index'
import { DefaultCard } from './cards/DefaultCard/index'
import { IntroCard } from './cards/IntroCard/index'

// Import card types
import type { SectionType } from '../../domain/index'
import type { Rect, Viewport } from './types'
import type { Label, Overlay } from '../../shared/Map/index'

// Import styles
import style from './index.css'
import '@nrk/dh-feature-components/dist/module/Slideshow/index.css'
import { Footer } from './cards/Footer/index'

export type Props = {
  sections: SectionType[],
  overlays: Overlay[],
  labels: Label[],
  viewport: Viewport,
  load: boolean,
  sendGaEvent?: any => void
}

export type State = {
  rect: Rect | null,
  visibleIndex: number
}

class MapStory extends Component<Props, State> {
  elm: HTMLDivElement

  constructor (props: Props) {
    super(props)

    this.state = {
      rect: null,
      visibleIndex: props.sections.length ? 0 : -1
    }
  }

  componentDidMount () {
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize = () => {
    const scrollTop = getScrollTop()
    const rect = this.elm.getBoundingClientRect()

    this.setState({
      rect: {
        height: rect.height,
        top: rect.top + scrollTop,
        bottom: rect.bottom + scrollTop
      }
    })
  }

  handleEnter = (entry: any, idx: number) => {
    this.setState({ visibleIndex: idx })

    const key = idx
    const section = this.props.sections[this.state.visibleIndex]

    this.props.sendGaEvent &&
    this.props.sendGaEvent({
      action: 'SHOW_CONTENT',
      label: `${key}/${section.type}(${section.title})`
    })
  }

  render () {
    const { sections, viewport, overlays, labels } = this.props
    const { visibleIndex } = this.state

    const activeSection = sections[visibleIndex]
    const blur = ['intro', 'footer'].indexOf(activeSection.type) !== -1
    const fullWidth = true

    const cardsInlineStyle = {}
    const mapContainerStyle = {}

    if (viewport && viewport.height > -1) {
      mapContainerStyle.height = `${viewport.height}px`
      mapContainerStyle.marginBottom = `-${viewport.height}px`
    }

    return (
      <div ref={elm => (this.elm = elm)} className={style.mapStory}>
        <div class={bem(style.mapStory__mapContainer, { blur })} style={mapContainerStyle}>
          <div class={style.mapStory__map}>
            <Map section={sections[visibleIndex]} overlays={overlays} labels={labels} fullWidth={fullWidth} />
          </div>
        </div>
        <ViewportIntersections
          className={style.mapStory__cards}
          onEnter={this.handleEnter}
          threshold={[0, 0.5, 1]}
          style={cardsInlineStyle}>
          {
            sections.map((section, idx) => {
              switch (section.type) {
                case 'intro':
                  return <IntroCard
                    key={String(idx)}
                    title={section.title}
                    lead={section.lead}
                    viewport={viewport} />
                case 'normal':
                  return <DefaultCard
                    key={String(idx)}
                    align={section.align}
                    title={section.title}
                    content={section.text}
                    viewport={viewport}
                    byline={section.byline}
                    date={section.date}
                    alt={section.caption || section.alternativeText}
                  />
                case 'footer':
                  return <Footer
                    credits={section.credits}
                    published={section.published}
                    facebookUrl={section.facebookUrl}
                    twitterUrl={section.twitterUrl} />
                default:
                  return <DefaultCard
                    key={String(idx)}
                    align={''}
                    content={'Ukjent innhold'}
                    viewport={viewport}
                  />
              }
            })
          }
        </ViewportIntersections>
      </div>
    )
  }
}

export default MapStory
